import { useStoreActions, useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Select } from '@ge/components/select';
import { SortDirection } from '@ge/components/table/models/sort-direction';
import { useColumnState } from '@ge/components/table/use-column-state';
import { DashboardDateFilter } from '@ge/feat-analyze/components/dashboard/dashboard-date-filter';
import { DashboardIECLossCategories } from '@ge/feat-analyze/components/dashboard/dashboard-iec-loss-categories';
import { DashboardKpiChart } from '@ge/feat-analyze/components/dashboard/dashboard-kpi-chart';
import { DashboardStatsHeader } from '@ge/feat-analyze/components/dashboard/dashboard-stats-header';
import useSiteIecData from '@ge/feat-analyze/data-hooks/use-site-iec-data';
import useSitesKpiData, {
  useSiteAssetKpiData,
} from '@ge/feat-analyze/data-hooks/use-sites-kpi-data';
import { FilterDefs, SiteKpiChartDefs } from '@ge/feat-analyze/models';
import { getKpiCategories } from '@ge/feat-analyze/util';
import { DateRange, EntityType, KpiCategoryDefs, TimeAggr } from '@ge/models/constants';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { getFilterDateRange } from '@ge/shared/util';
import { killEventPropagation } from '@ge/shared/util/general';

import { TabDetailContainer } from '../../entity-details-shared';
import AssetPerformanceTable from '../asset-performance-table';

const CHART_HEIGHT = 280;

const AssetMetric = {
  NAME: 'name',
  STATE: 'metrics.state',
};

const ChartType = {
  ACTUAL_VS_EXPECTED_PRODUCTION: 'actualVsExpectedProduction',
  TBA: 'tba',
  UNPRODUCED_ENERGY: 'unproducedEnergy',
};

const chartDefMap = {
  [ChartType.ACTUAL_VS_EXPECTED_PRODUCTION]:
    SiteKpiChartDefs[FilterDefs.PERFORMANCE].actual_vs_expected_production,
  [ChartType.TBA]: SiteKpiChartDefs[FilterDefs.AVAILABILITY].tba,
};

const getKpiCategoriesByDef = (def = []) =>
  Array.from(
    new Set([
      // these are always included for the kpi header and table
      KpiCategoryDefs.CAPACITY_FACTOR,
      KpiCategoryDefs.EVENT_COVERAGE,
      KpiCategoryDefs.PRODUCTION_ACTUAL,
      // mvp0 - no learned data available yet so only contract
      KpiCategoryDefs.UNPRODUCED_ENERGY_CONTRACT,
      KpiCategoryDefs.REPORTING_ASSETS_RATIO,
      KpiCategoryDefs.TBA,
      ...getKpiCategories(def),
    ]),
  );

const StyledTabDetailContainer = styled(TabDetailContainer)`
  position: relative;
`;

const DashboardContainer = styled.div`
  margin: 0px 15px 30px 10px;
  background-color: ${(props) => props.theme.entityDetails.sitePanelBlockBackground};
`;

const DashboardHeaderContainer = styled(DashboardContainer)`
  > div {
    align-items: center;
    justify-content: center;
    margin: 10px 0 14px;

    > div {
      border-left-color: ${(props) => props.theme.entityDetails.sitePanelBlockBorder};
    }
  }
  .kpi-title {
    h3 {
      margin-bottom: 0;
    }
  }
`;

const DashboardChartContainer = styled(DashboardContainer)`
  padding: 16px 5px 30px;

  .chart-header {
    h2 {
      margin-left: 20px;
    }

    .chart-select {
      margin: 10px auto 4px 8px;
    }
  }

  .chart {
    padding: 0;
  }
`;

const StyledHeader = styled.h2`
  font-size: 18px;
  margin-left: 35px;
  margin-bottom: 0 !important;
`;

const AssetsTableContainer = styled.div`
  margin-bottom: 40px;

  table {
    border-top: 0 !important;
    th {
      border-right: 0;
    }
  }
`;

const DateFilterContainer = styled.div`
  display: flex;
  flex-flow: row-reverse;
  margin: 4px 15px;
`;

const SitePerformance = ({ siteId, isDatePersist, popsout }) => {
  // state
  const [{ categories, def, type }, setChartState] = useState(() => {
    const type = ChartType.TBA;
    const def = chartDefMap[type];

    return {
      categories: getKpiCategoriesByDef(def),
      def,
      type,
    };
  });

  // store
  let { dateRange: dates } = useStoreState(({ analyze }) => analyze);
  const { panelDateRange } = useStoreState(({ analyze }) => analyze);
  const { updatePanelDateRange } = useStoreActions(({ analyze }) => analyze);
  const getSiteById = useStoreState((state) => state.sites.getSiteById);
  const [dateRange, setDateRange] = useState({
    startDate: undefined,
    endDate: undefined,
  });
  if (popsout) {
    dates = panelDateRange;
  }
  useEffect(() => {
    if (!popsout) {
      updatePanelDateRange(dates);
    }
  }, [popsout, updatePanelDateRange, dates]);

  useEffect(() => {
    // don't notify if custom date range is incomplete
    if (dates.range === DateRange.CUSTOM && !(dates.endDate && dates.startDate)) {
      return;
    }

    const filterDateRange = getFilterDateRange({
      startDate: dates.startDate,
      range: dates.range,
      endDate: dates.endDate,
    });

    setDateRange({
      startDate: filterDateRange.startDate.entityTimezone,
      endDate: filterDateRange.endDate.entityTimezone,
    });
  }, [dates]);

  const onDateFilterChange = ({ endDate, range, startDate }) => {
    const filterDateRange = getFilterDateRange({ endDate, range, startDate });
    setDateRange({
      startDate: filterDateRange.startDate.entityTimezone,
      endDate: filterDateRange.endDate.entityTimezone,
    });
  };

  // hooks
  const { t } = useTranslation(['analyze.entity-panel'], { useSuspense: false });
  const { sortDirection, sortMetric, updateSortMetric } = useColumnState({
    defaultSortMetric: AssetMetric.STATE,
    defaultSortDirection: SortDirection.DESC,
  });
  const { endDate, startDate } = dateRange;

  // data hooks
  const {
    data: siteKpiData,
    error: siteKpiError,
    isLoading: isSiteKpiLoading,
  } = useSitesKpiData({
    def: categories,
    categories,
    endDate,
    siteIds: [siteId],
    startDate,
    seriesEntityAggr: 'Wind_Turbine',
    dateEntityAggr: 'Site',
    timeAggr: TimeAggr.DAILY,
  });

  const { data: assets } = useSiteAssetKpiData(siteId, {
    isLoading: isSiteKpiLoading,
    kpiData: siteKpiData,
    sortMetric,
    sortDirection,
    dateRange,
  });

  const {
    data: iecCategoryData,
    error: iecCategoryError,
    isLoading: isIecLoading,
  } = useSiteIecData({
    category: KpiCategoryDefs.UNPRODUCED_ENERGY_CONTRACT,
    endDate,
    // can remove but only fetching when chart is active
    isActive: type === ChartType.UNPRODUCED_ENERGY,
    siteId,
    startDate,
  });

  // context
  const { showAssetDetails } = useContext(EntityDetailsContext);

  // handlers
  const handleSelectChange = useCallback(({ value: type }) => {
    const def = chartDefMap[type];
    const categories = getKpiCategoriesByDef(def);

    setChartState({
      categories,
      def,
      type,
    });
  }, []);

  // Respond to selection in asset table
  const handleAssetSelect = useCallback(
    (e, asset) => {
      killEventPropagation(e);
      showAssetDetails(asset.id);
    },
    [showAssetDetails],
  );

  const site = getSiteById(siteId);

  const translations = {
    actualVsExpectedProductionLabel: t(
      'actual_vs_expected_production_label',
      'Actual vs Expected Production',
    ),
    assetsTitle: t('assets_title', 'Assets'),
    performanceTitle: t('performance_title', 'Performance'),
    tbaTechnicalLabel: t('tba_technical_label', 'TBA-Technical'),
    unproducedEnergyLabel: t('unproduced_energy_label', 'Unproduced Energy'),
  };

  const selectOptions = [
    { label: translations.tbaTechnicalLabel, value: ChartType.TBA },
    {
      label: translations.actualVsExpectedProductionLabel,
      value: ChartType.ACTUAL_VS_EXPECTED_PRODUCTION,
    },
    { label: translations.unproducedEnergyLabel, value: ChartType.UNPRODUCED_ENERGY },
  ];

  const renderChart = () => {
    if (type === ChartType.UNPRODUCED_ENERGY) {
      return (
        <DashboardIECLossCategories
          data={iecCategoryData}
          entityType={EntityType.SITE}
          error={iecCategoryError}
          height={CHART_HEIGHT}
          hideBorder
          isLoading={isIecLoading}
          namespace="analyze.dashboard"
          showHover={false}
          showTitle={false}
        />
      );
    }

    return (
      <DashboardKpiChart
        chartDef={def.graph1}
        className="chart"
        data={siteKpiData}
        entityType={EntityType.TURBINE}
        error={siteKpiError}
        height={CHART_HEIGHT}
        hideBorder
        isLoading={isSiteKpiLoading}
        namespace="analyze.dashboard"
        parentEntity={site}
        showTitle={false}
        showToggle={false}
        xAxisTitle={translations.assetsTitle}
      />
    );
  };

  return (
    <StyledTabDetailContainer>
      <DateFilterContainer>
        <DashboardDateFilter
          noCustom
          noPreview
          timezone={site?.timezone}
          isDatePersist={isDatePersist}
          onChange={!isDatePersist ? onDateFilterChange : undefined}
          popsout={popsout}
          isPanelDateSelector
        />
      </DateFilterContainer>
      <DashboardHeaderContainer>
        <DashboardStatsHeader data={siteKpiData} />
      </DashboardHeaderContainer>
      <DashboardChartContainer>
        <div className="chart-header">
          <StyledHeader>{translations.performanceTitle}</StyledHeader>
          <Select
            className="chart-select"
            onChange={handleSelectChange}
            options={selectOptions}
            value={selectOptions.find(({ value }) => value === type)}
          />
        </div>
        {renderChart()}
      </DashboardChartContainer>
      <StyledHeader>{translations.assetsTitle}</StyledHeader>
      <AssetsTableContainer>
        <AssetPerformanceTable
          assets={assets}
          sortAction={updateSortMetric}
          sortMetric={sortMetric}
          sortDirection={sortDirection}
          onAssetSelect={handleAssetSelect}
        />
      </AssetsTableContainer>
    </StyledTabDetailContainer>
  );
};

SitePerformance.propTypes = {
  siteId: PropTypes.string.isRequired,
  isDatePersist: PropTypes.bool,
  popsout: PropTypes.bool,
};

export default SitePerformance;
